import React from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { Copy, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { generateApiKey, generateSecretKey } from "@/shared/utils/utilities";

export default function ApiTab({ t, onKeysRegenerated }) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const apiKey = watch("apiKey");
  const secretKey = watch("secretKey");

  const regenerateKeys = () => {
    if (onKeysRegenerated) {
      onKeysRegenerated();
    }
    setValue("apiKey", generateApiKey(), { shouldDirty: true });
    setValue("secretKey", generateSecretKey(), { shouldDirty: true });
    toast.success(t("apiKeysRegenerated") || "API Keys regenerated successfully");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("copiedToClipboard") || "Copied to clipboard");
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>{t("security")}:</strong> {t("apiKeysShownOnce")}
        </p>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label>{t("apiKeyPublic")}</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={regenerateKeys}
            className="gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            {t("regenerate")}
          </Button>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={t("apisettingsapiKeyPlaceholder")}
            value={apiKey}
            readOnly
            className="font-mono text-sm bg-slate-50"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => copyToClipboard(apiKey)}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t("secretKey")}</Label>
        <div className="flex gap-2">
          <Input
            placeholder={t("apisettingssecretKeyPlaceholder")}
            value={secretKey}
            readOnly
            className="font-mono text-sm bg-slate-50"
            type="password"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => copyToClipboard(secretKey)}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t("callbackUrl")}</Label>
        <Input
          {...register("callback", {
            pattern: {
              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
              message: t("invalidUrl") || "Invalid URL format",
            },
          })}
          placeholder={t("callbackUrlPlaceholder")}
          className={errors.callback ? "border-red-500 focus:ring-red-500/20" : ""}
        />
        {errors.callback && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {errors.callback.message}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <Label>{t("webhookUrl")}</Label>
        <Input
          {...register("webhook", {
            pattern: {
              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
              message: t("invalidUrl") || "Invalid URL format",
            },
          })}
          placeholder={t("webhookUrlPlaceholder")}
          className={errors.webhook ? "border-red-500 focus:ring-red-500/20" : ""}
        />
        {errors.webhook && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {errors.webhook.message}
          </p>
        )}
      </div>
    </div>
  );
}
